import React, { useEffect, useState, useMemo, useCallback } from "react";
import { Link } from "react-router-dom";
import PosModals from "../../core/modals/pos-modal/posModals";
import PropTypes from "prop-types";
// import axios from "axios";
import { mockPos } from "../../api/mock/pos";
import ImageWithBasePath from "../../core/img/imagewithbasebath";

// Web Apis
import { fetchWebCategories } from "../../api/website-api/web-category";
import {
  fetchWebProducts,
  fetchWebProductById,
} from "../../api/website-api/web-product";
import {
  createPosOrder,
  buildPosOrderPayload,
} from "../../api/website-api/web-pos-order";
import { verifyCoupon } from "../../api/website-api/web-coupon";
import { fetchRefundPolicy } from "../../api/website-api/pagesApi";

import "./css/pos.css";

// const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

// --- image URL helpers ---
const isHttpUrl = (v) => typeof v === "string" && /^https?:\/\//i.test(v);
const looksLikeCatalog = (v) =>
  typeof v === "string" && /^\/?(catalog|catelog)\//i.test(v);
const normalizeKey = (s = "") => {
  let v = String(s)
    .trim()
    .replace(/^\/+/, "")
    .replace(/^catelog\//i, "catalog/");
  if (!/^catalog\//i.test(v)) v = `catalog/${v.split("/").pop()}`;
  return v;
};
const toPublicUrl = (v) => {
  if (!v) return "";
  if (isHttpUrl(v)) return v;
  if (looksLikeCatalog(v)) {
    const base = (
      process.env.REACT_APP_API_IMAGE_BASE_URL ||
      "https://hamilton-shop-images.s3.ap-south-1.amazonaws.com"
    ).replace(/\/+$/, "");
    return `${base}/${normalizeKey(v)}`;
  }
  return v;
};

//Helper to detect "no store" 403 errors
const noStore403 = (err) => {
  const msg = err?.response?.data?.message || err?.message || "";
  const code = err?.response?.status;
  return code === 403 && /no\s*store/i.test(msg);
};

const Pos = () => {
  const [activeTab, setActiveTab] = useState("all");
  const [demo, setDemo] = useState(false);

  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);

  const categoryMap = useMemo(() => {
    const map = {};
    categories.forEach((cat) => {
      const id = String(cat._id || cat.category_id || cat.id);
      map[id] = cat.name;
    });
    return map;
  }, [categories]);

  const [selectedItems, setSelectedItems] = useState([]);
  const [productCache, setProductCache] = useState({});

  const [receivedAmount, setReceivedAmount] = useState("");
  const [payingAmount, setPayingAmount] = useState(0);
  const [change, setChange] = useState(0);
  const [paymentType, setPaymentType] = useState({
    value: "cash",
    label: "Cash",
  });

  // Reference number for knet/card payments
  const [invoiceNo, setInvoiceNo] = useState("");

  const [paymentReceiver, setPaymentReceiver] = useState("");
  const [paymentNote, setPaymentNote] = useState("");
  const [saleNote, setSaleNote] = useState("");
  const [staffNote, setStaffNote] = useState("");

  // Split payment state
  const [paymentSplits, setPaymentSplits] = useState([
    { payment_type: "cash", amount: "", reference: "" },
    { payment_type: "card", amount: "", reference: "" },
  ]);

  // Customer info state for order creation
  const [customerInfo, setCustomerInfo] = useState({
    firstname: "",
    lastname: "",
    email: "",
    telephone: "",
    company: "",
    address_1: "",
    address_2: "",
    city: "Kuwait City",
    postcode: "",
    country_id: 114,
    zone_id: 1789,
    area_id: 153,
  });

  const [lastTransaction, setLastTransaction] = useState(null);
  const [invoiceLang, setInvoiceLang] = useState("en");
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 18;
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Coupon State
  const [couponCode, setCouponCode] = useState("");
  const [couponData, setCouponData] = useState(null);
  const [couponDiscount, setCouponDiscount] = useState(0);
  const [loadingCoupon, setLoadingCoupon] = useState(false);

  // Server-side pagination state
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [uiAlert, setUiAlert] = useState({
    open: false,
    title: "",
    message: "",
    messageClassName: "",
    tone: "warning",
    lang: "en",
    confirmText: "OK",
    centerMessage: false,
  });

  const resolveUiLang = () => {
    const lang = String(localStorage.getItem("lang") || "en").toLowerCase();
    return lang.startsWith("ar") ? "ar" : "en";
  };

  const closeUiAlert = useCallback(() => {
    setUiAlert((prev) => ({ ...prev, open: false }));
  }, []);

  const showUiAlert = useCallback((message, options = {}) => {
    const lang = resolveUiLang();
    const isArabic = lang === "ar";
    const tone = options.tone || "warning";
    const fallbackTitleByTone = {
      success: isArabic ? "نجاح" : "Success",
      error: isArabic ? "تنبيه" : "Alert",
      warning: isArabic ? "تنبيه" : "Notice",
      info: isArabic ? "معلومة" : "Info",
    };

    setUiAlert({
      open: true,
      title:
        options.title || fallbackTitleByTone[tone] || fallbackTitleByTone.info,
      message: String(message || ""),
      messageClassName: String(options.messageClassName || ""),
      tone,
      lang,
      confirmText: options.confirmText || (isArabic ? "حسناً" : "OK"),
      centerMessage: Boolean(options.centerMessage),
    });
  }, []);

  // Debounce search input by 1 second
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 1000);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => {
    if (!uiAlert.open) return undefined;

    const onEscape = (event) => {
      if (event.key === "Escape") closeUiAlert();
    };

    document.addEventListener("keydown", onEscape);
    return () => document.removeEventListener("keydown", onEscape);
  }, [uiAlert.open, closeUiAlert]);

  useEffect(() => {
    if (!uiAlert.open) return undefined;

    const oldOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = oldOverflow;
    };
  }, [uiAlert.open]);

  const labels = {
    en: {
      invoice: "Invoice",
      orderId: "Order ID",
      date: "Date",
      paymentType: "Payment Type",
      cashier: "Payment Receiver",
      items: "Items",
      qty: "Qty",
      price: "Price",
      lineTotal: "Total",
      subtotal: "Subtotal",
      total: "Total",
      received: "Received",
      paying: "Paying",
      change: "Change",
      thankYou: "Thank you for your purchase!",
      invoiceNo: "Invoice No",
      refundPolicy: "Refund Policy",
      printSave: "Print / Save as PDF",
      back: "Back",
      printTip: "Tip: In the print dialog choose Save as PDF to download.",
      noPolicy: "Refund policy is not available.",
      splitDetails: "Split Payment Details",
      splitMethod: "Method",
      splitAmount: "Amount",
      splitReference: "Reference",
      splitNoReference: "-",
    },
    ar: {
      invoice: "فاتورة",
      orderId: "رقم الطلب",
      date: "التاريخ",
      paymentType: "طريقة الدفع",
      cashier: "المستلم",
      items: "العناصر",
      qty: "الكمية",
      price: "السعر",
      lineTotal: "الإجمالي",
      subtotal: "الإجمالي الفرعي",
      total: "الإجمالي",
      received: "المبلغ المستلم",
      paying: "المبلغ المدفوع",
      change: "الباقي",
      thankYou: "شكراً لتسوقكم!",
      invoiceNo: "رقم الفاتورة",
      refundPolicy: "سياسة الاسترجاع",
      printSave: "طباعة / حفظ PDF",
      back: "رجوع",
      printTip: "ملاحظة: من نافذة الطباعة اختر حفظ كملف PDF للتنزيل.",
      noPolicy: "سياسة الاسترجاع غير متوفرة.",
    },
  };

  const nf = (n, lang) =>
    (typeof n === "number" ? n : Number(n || 0)).toLocaleString(
      lang === "ar" ? "ar-SA" : "en-US",
      { minimumFractionDigits: 2, maximumFractionDigits: 2 },
    );

  const escapeHtml = (value = "") =>
    String(value)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;");

  const paymentTypeLabel = (value = "") => {
    const v = String(value || "").trim();
    const k = v.toLowerCase();
    if (k.includes("cash")) return "Cash";
    if (k.includes("card")) return "Card";
    if (k.includes("knet")) return "KNET";
    if (k.includes("split")) return "Split";
    return v || "-";
  };

  const extractSplitPayments = (tx = {}) => {
    const candidateArrays = [
      tx.paymentSplits,
      tx.payment_splits,
      tx.order?.payment_splits,
      tx.data?.payment_splits,
    ];

    const rawSplits = candidateArrays.find(
      (arr) => Array.isArray(arr) && arr.length > 0,
    ) || [];

    return rawSplits
      .map((split) => ({
        method: paymentTypeLabel(
          split?.payment_type ||
            split?.payment_method ||
            split?.method ||
            split?.type,
        ),
        amount: Number(
          split?.amount ?? split?.paid_amount ?? split?.value ?? split?.total ?? 0,
        ),
        reference: String(
          split?.reference ||
            split?.ref ||
            split?.transaction_reference ||
            split?.transaction_id ||
            "",
        ),
      }))
      .filter(
        (split) =>
          split.method !== "-" ||
          Number(split.amount || 0) > 0 ||
          Boolean(split.reference),
      );
  };

  const normalizePolicyText = (value) => {
    if (!value) return "";

    const raw = String(value).trim();

    const withLineBreaks = raw
      .replace(/<\s*br\s*\/?>/gi, "\n")
      .replace(/<\/(p|div|li|h[1-6]|tr|table)>/gi, "\n");

    const stripped = withLineBreaks.replace(/<[^>]*>/g, " ");

    const decoded = stripped
      .replace(/&nbsp;/gi, " ")
      .replace(/&amp;/gi, "&")
      .replace(/&lt;/gi, "<")
      .replace(/&gt;/gi, ">")
      .replace(/&quot;/gi, '"')
      .replace(/&#39;/gi, "'");

    return decoded
      .split(/\r?\n/)
      .map((line) => line.replace(/\s+/g, " ").trim())
      .filter(Boolean)
      .join("\n");
  };

  const buildInvoiceHtml = (tx, lang = "en", refundPolicy = "") => {
    const L = labels[lang] || labels.en;
    const enLabels = labels.en;
    const dir = lang === "ar" ? "rtl" : "ltr";
    const alignStart = lang === "ar" ? "right" : "left";
    const alignEnd = lang === "ar" ? "left" : "right";
    const splitPayments = extractSplitPayments(tx);
    const paymentTypeRaw = String(tx?.type || tx?.payment_type || "").toLowerCase();
    const isSplitPayment =
      paymentTypeRaw.includes("split") || splitPayments.length > 0;

    const subtotal2 = (tx.items || []).reduce(
      (sum, it) => sum + Number(it.price || 0) * Number(it.quantity || 0),
      0,
    );

    const total =
      typeof tx.totalAmount === "number" ? tx.totalAmount : subtotal2;
    const received = tx.receivedAmount ?? 0;
    const paying = tx.payingAmount ?? total;
    const changeVal = tx.change ?? Math.max(0, received - paying);

    const todayStr = new Date(tx.createdAt || Date.now()).toLocaleString(
      lang === "ar" ? "ar-SA" : "en-US",
    );

    const rows = (tx.items || [])
      .map(
        (it) => `
      <tr>
        <td>${it.name || ""}</td>
        <td style="text-align:center">${nf(it.quantity || 0, lang)}</td>
        <td style="text-align:${alignEnd}">${nf(it.price || 0, lang)}</td>
        <td style="text-align:${alignEnd}">${nf(
          (it.price || 0) * (it.quantity || 0),
          lang,
        )}</td>
      </tr>`,
      )
      .join("");

    const invoiceNoRow = tx.invoiceNo
      ? `<div><span>${L.invoiceNo}</span><strong>${tx.invoiceNo}</strong></div>`
      : "";
    const splitDetailsSection =
      isSplitPayment && splitPayments.length
        ? `
    <div class="split-block">
      <div class="split-title">${L.splitDetails || enLabels.splitDetails}</div>
      <table class="split-table">
        <thead>
          <tr>
            <th class="start">${L.splitMethod || enLabels.splitMethod}</th>
            <th class="end">${L.splitAmount || enLabels.splitAmount}</th>
            <th class="start">${L.splitReference || enLabels.splitReference}</th>
          </tr>
        </thead>
        <tbody>
          ${splitPayments
            .map(
              (split) => `
            <tr>
              <td>${escapeHtml(split.method || "-")}</td>
              <td class="end">${nf(split.amount || 0, lang)}</td>
              <td>${escapeHtml(
                split.reference || L.splitNoReference || enLabels.splitNoReference,
              )}</td>
            </tr>
          `,
            )
            .join("")}
        </tbody>
      </table>
    </div>
  `
        : "";

    const suggestedFileName = `Invoice-${tx.orderId || "order"}.pdf`;
    const storeWebsiteUrl = "https://nomination.kw/";

    const preparedPolicyText = normalizePolicyText(refundPolicy || L.noPolicy);
    const preparedPolicyPrintHtml = escapeHtml(preparedPolicyText).replace(
      /\n/g,
      "<br/>",
    );

    return `
<!DOCTYPE html>
<html lang="${lang}" dir="${dir}">
<head>
<meta charset="utf-8" />
<meta name="viewport" content="width=device-width,initial-scale=1" />
<title>${L.invoice} - ${tx.orderId || ""}</title>

<style>
  :root { color-scheme: light; }

  * { box-sizing: border-box; }

  body {
    font-family: Arial, "Tahoma", "Segoe UI", sans-serif;
    margin: 0;
    padding: 12px;
    background: #f6f6f6;
    direction: ${dir};
    text-align: ${alignStart};
    color: #111827;
  }

  .wrap {
    width: 100%;
    max-width: 900px;
    margin: 0 auto;
    background: #fff;
    border: 1px solid #e5e7eb;
    border-radius: 14px;
    padding: 18px;
  }

  h1 {
    font-size: 20px;
    margin: 0 0 14px;
    text-align: center;
    color: #111827;
  }

  .meta {
    font-size: 13px;
    margin-bottom: 16px;
    background: #fafafa;
    border: 1px solid #eee;
    border-radius: 10px;
    padding: 10px 12px;
  }

  .meta div {
    display: flex;
    gap: 8px;
    justify-content: space-between;
    padding: 5px 0;
    align-items: flex-start;
  }

  .meta span {
    opacity: .75;
  }

  .meta strong {
    text-align: ${alignEnd};
    word-break: break-word;
  }

  .split-block {
    margin: 10px 0 12px;
    border: 1px solid #ececec;
    border-radius: 10px;
    overflow: hidden;
  }

  .split-title {
    padding: 8px 12px;
    font-size: 13px;
    font-weight: 700;
    background: #f8fafc;
    border-bottom: 1px solid #ececec;
  }

  .split-table {
    margin-top: 0;
    font-size: 12.5px;
  }

  .split-table th, .split-table td {
    padding: 8px 10px;
    border-bottom: 1px solid #f0f0f0;
  }

  .split-table tbody tr:last-child td {
    border-bottom: 0;
  }

  table {
    width: 100%;
    border-collapse: collapse;
    font-size: 13px;
    margin-top: 6px;
  }

  th, td {
    padding: 10px 8px;
    border-bottom: 1px dashed #ddd;
    vertical-align: top;
  }

  th {
    text-align: ${alignStart};
    font-weight: bold;
    background: #f8f8f8;
  }

  tfoot td {
    border: 0;
    padding: 7px 8px;
  }

  .total-row td {
    border-top: 2px solid #000;
    font-weight: bold;
    padding-top: 10px;
  }

  .thanks {
    text-align: center;
    margin-top: 16px;
    font-size: 13px;
    font-weight: 600;
  }

  .end { text-align: ${alignEnd}; }
  .start { text-align: ${alignStart}; }

  .actions {
    display: flex;
    gap: 10px;
    justify-content: center;
    align-items: center;
    margin-bottom: 12px;
    flex-wrap: wrap;
  }

  .btn {
    border: 0;
    border-radius: 10px;
    padding: 10px 14px;
    font-size: 14px;
    cursor: pointer;
  }

  .btn-primary { background: #0d6efd; color: #fff; }
  .btn-light { background: #f0f0f0; color: #111; }

  .hint {
    font-size: 12px;
    opacity: .75;
    text-align: center;
    margin-top: 4px;
    margin-bottom: 12px;
  }

  .policy-plain {
    margin-top: 10px;
    text-align: center;
    font-size: 13px;
    line-height: 1.6;
    font-weight: 600;
    word-break: break-word;
    direction: ${dir};
  }

  .store-url {
    margin-top: 10px;
    text-align: center;
    font-size: 12px;
    color: #6b7280;
    word-break: break-word;
  }

  @media (max-width: 480px) {
    body { padding: 10px; }
    .wrap { padding: 12px; border-radius: 10px; }
    table { font-size: 12px; }
    th, td { padding: 7px 5px; }
    h1 { font-size: 16px; }
    .policy-plain { font-size: 12px; }
    .store-url { font-size: 11px; }
  }

  @media print {
    body {
      background: #fff;
      padding: 0;
    }

    .wrap {
      border: 0;
      border-radius: 0;
      padding: 0;
      max-width: 100%;
    }

    .actions, .hint {
      display: none !important;
    }

    .policy-plain {
      page-break-inside: avoid;
      break-inside: avoid;
      margin-top: 10px;
    }
  }
</style>
</head>

<body>
  <div class="wrap">
    <div class="actions">
      <button class="btn btn-primary" onclick="doPrint()">
        ${L.printSave}
      </button>
      <button class="btn btn-light" onclick="goBack()">
        ${L.back}
      </button>
    </div>

    <div class="hint">${L.printTip}</div>

    <h1>${L.invoice} / ${lang === "ar" ? "Invoice" : "فاتورة"}</h1>

    <div class="meta">
      <div><span>${L.orderId}</span><strong>${tx.orderId || "-"}</strong></div>
      <div><span>${L.date}</span><strong>${todayStr}</strong></div>
      <div><span>${L.paymentType}</span><strong>${tx.type || "-"}</strong></div>
      ${invoiceNoRow}
      <div><span>${L.cashier}</span><strong>${tx.paymentReceiver || "-"}</strong></div>
    </div>
    ${splitDetailsSection}

    <table>
      <thead>
        <tr>
          <th class="start">${L.items}</th>
          <th style="text-align:center">${L.qty}</th>
          <th class="end">${L.price}</th>
          <th class="end">${L.lineTotal}</th>
        </tr>
      </thead>
      <tbody>
        ${rows || `<tr><td colspan="4" style="text-align:center">—</td></tr>`}
      </tbody>
      <tfoot>
        <tr>
          <td colspan="3" class="end">${L.subtotal}</td>
          <td class="end">${nf(subtotal2, lang)}</td>
        </tr>
        <tr class="total-row">
          <td colspan="3" class="end">${L.total}</td>
          <td class="end">${nf(total, lang)}</td>
        </tr>
        <tr>
          <td colspan="3" class="end">${L.received}</td>
          <td class="end">${nf(received, lang)}</td>
        </tr>
        <tr>
          <td colspan="3" class="end">${L.paying}</td>
          <td class="end">${nf(paying, lang)}</td>
        </tr>
        <tr>
          <td colspan="3" class="end">${L.change}</td>
          <td class="end">${nf(changeVal, lang)}</td>
        </tr>
      </tfoot>
    </table>

    <div class="thanks">${L.thankYou}</div>
    <div class="policy-plain">${L.refundPolicy}: ${preparedPolicyPrintHtml}</div>
    <div class="store-url">${storeWebsiteUrl}</div>
  </div>

  <script>
    document.title = ${JSON.stringify(suggestedFileName)};

    function doPrint() {
      window.print();
    }

function goBack() {
  window.location.href = window.location.origin + "/pos";
}
  </script>
</body>
</html>
`;
  };

  const printInvoice = async (lang = invoiceLang, tx = lastTransaction) => {
  if (!tx) {
    showUiAlert(
      window.translate(
        "No completed transaction to print. Please place an order first."
      ),
      { tone: "warning" },
    );
    return;
  }

  let refundPolicy = "";

  try {
    const res = await fetchRefundPolicy(lang);

    refundPolicy =
      res?.data?.description?.description ||
      res?.description?.description ||
      res?.data?.information_description?.description ||
      res?.information_description?.description ||
      res?.data?.content ||
      res?.content ||
      "";

    console.log("Refund Policy API:", res);
    console.log("Refund Policy HTML:", refundPolicy);
  } catch (error) {
    console.error("Failed to fetch refund policy:", error);
    refundPolicy = "";
  }

  const html = buildInvoiceHtml(tx, lang, refundPolicy);

  const w = window.open("", "_self");
  if (!w) {
    showUiAlert(window.translate("Unable to open invoice."), { tone: "error" });
    return;
  }

  w.document.open();
  w.document.write(html);
  w.document.close();
};

  // Fetch the categories
  useEffect(() => {
    const loadCategories = async () => {
      try {
        const categoryData = await fetchWebCategories();

        const normalized = categoryData.map((cat) => {
          const raw = cat.image || cat.Image || "";

          // Check if we have a valid image
          const hasImage = raw && raw !== "" && raw !== "category-default.svg";

          const isLocalAsset =
            hasImage &&
            !/^data:image/i.test(raw) &&
            !isHttpUrl(raw) &&
            !looksLikeCatalog(raw);

          let imageUrl = "";
          if (hasImage) {
            imageUrl = /^data:image/i.test(raw)
              ? raw
              : isLocalAsset
                ? `assets/img/categories/${raw}`
                : toPublicUrl(raw);
          }

          const idVal = cat._id || cat.id || cat.CategoryId || cat.category_id;

          return {
            _id: String(idVal).trim(),
            name: cat.name || cat.CategoryName || cat.title || "Unnamed",
            imageUrl,
            isLocalAsset,
            hasImage, // flag to know if we should show fallback
          };
        });

        setCategories([
          {
            _id: "all",
            name: `${window.translate("All")}`,
            imageUrl: "assets/img/categories/category-01.svg",
            isLocalAsset: true,
            hasImage: true,
          },
          ...normalized,
        ]);

        setDemo(false);
      } catch (e) {
        console.error("Failed to fetch categories from website:", e);

        setDemo(true);
        const normalized = mockPos.categories.map((cat) => {
          const raw = cat.image || "category-default.svg";
          const isLocalAsset =
            !/^data:image/i.test(raw) &&
            !isHttpUrl(raw) &&
            !looksLikeCatalog(raw);
          const imageUrl = /^data:image/i.test(raw)
            ? raw
            : isLocalAsset
              ? `assets/img/categories/${raw}`
              : toPublicUrl(raw);
          return {
            _id: String(cat.id).trim().toLowerCase(),
            name: cat.name,
            imageUrl,
            isLocalAsset,
          };
        });
        setCategories([
          {
            _id: "all",
            name: `${window.translate("All")}`,
            imageUrl: "assets/img/categories/category-01.svg",
            isLocalAsset: true,
          },
          ...normalized,
        ]);
      }
    };

    loadCategories();
  }, []);

  // Fetch products from server with pagination, category, and search filters
  const loadProducts = useCallback(async () => {
    setIsLoading(true);
    try {
      const categoryId = activeTab === "all" ? undefined : activeTab;

      const response = await fetchWebProducts({
        page: currentPage,
        pagelimit: pageSize,
        search: debouncedSearch,
        categoryId,
        status: 1,
      });

      const { products: webProducts, count } = response;

      setTotalCount(count);

      const activeProducts = webProducts.filter(
        (p) => Number(p?.status ?? 1) === 1,
      );

      const normalizedProducts = activeProducts.map((p) => {
        const rawImage = p.image || p.thumb || "product-default.jpg";

        const isLocalAsset =
          !/^data:image/i.test(rawImage) &&
          !isHttpUrl(rawImage) &&
          !looksLikeCatalog(rawImage);

        const imageUrl = /^data:image/i.test(rawImage)
          ? rawImage
          : isLocalAsset
            ? `assets/img/products/${rawImage}`
            : toPublicUrl(rawImage);

        // ---- category id + name mapping ----
        const primaryCatId =
          Array.isArray(p.categories) && p.categories.length
            ? String(p.categories[0]) // e.g. "214"
            : String(p.category_id || ""); // fallback

        const categoryName =
          categoryMap[primaryCatId] ||
          p.category_name ||
          p.category ||
          p.Category ||
          "";

        // ---- Calculate discount price from discount_data ----
        const originalPrice = Number(p.price ?? p.Price ?? 0);
        let discountPrice = null;
        let discountPercentage = 0;

        if (Array.isArray(p.discount_data) && p.discount_data.length > 0) {
          // Find the best applicable discount (lowest price or highest percentage)
          const now = new Date();

          for (const discount of p.discount_data) {
            // Check if discount is valid based on date range
            const startDate = discount.date_start
              ? new Date(discount.date_start)
              : null;
            const endDate = discount.date_end
              ? new Date(discount.date_end)
              : null;

            const isValidDate =
              (!startDate || now >= startDate) && (!endDate || now <= endDate);

            if (!isValidDate) continue;

            // Check quantity requirements
            const minQty = Number(discount.quantity || 1);
            if (minQty > 1) continue; // Skip bulk discounts for display

            // Calculate discount based on type
            let calculatedDiscount = 0;

            if (discount.price && Number(discount.price) > 0) {
              // Fixed price discount
              calculatedDiscount = originalPrice - Number(discount.price);
            } else if (discount.percentage && Number(discount.percentage) > 0) {
              // Percentage discount
              calculatedDiscount =
                originalPrice * (Number(discount.percentage) / 100);
            }

            if (calculatedDiscount > 0) {
              const newPrice = originalPrice - calculatedDiscount;
              if (discountPrice === null || newPrice < discountPrice) {
                discountPrice = newPrice;
                discountPercentage = Math.round(
                  (calculatedDiscount / originalPrice) * 100,
                );
              }
            }
          }
        }

        return {
          ...p,
          categories: p.categories || [], // keep original ids
          _id: String(p.product_id || p.id || p._id),
          Product_Name: p.name || p.Product_Name || "Unnamed",
          Category: categoryName, // 👈 nice name
          CategoryId: primaryCatId, // 👈 first category id
          image: imageUrl,
          Price: discountPrice !== null ? discountPrice : originalPrice, // Use discounted price for calculations
          OriginalPrice: originalPrice, // Keep original for display
          DiscountPrice: discountPrice, // Discounted price (null if no discount)
          DiscountPercentage: discountPercentage, // Percentage off
          HasDiscount: discountPrice !== null && discountPrice < originalPrice,
          Qty: Number(p.quantity ?? p.Qty ?? 0),
          quantity: 1,
        };
      });

      setProducts(normalizedProducts);
      setDemo(false);
    } catch (e) {
      console.error("Failed to load products from website:", e);
    } finally {
      setIsLoading(false);
    }
  }, [activeTab, currentPage, debouncedSearch, categoryMap]);

  // Load products when dependencies change
  useEffect(() => {
    if (Object.keys(categoryMap).length > 0) {
      loadProducts();
    }
  }, [loadProducts, categoryMap]);

  // Products are already filtered/paginated by server, so just use them directly
  const displayProducts = products.filter(
    (product) => Number(product?.status ?? 1) === 1,
  );

  const totalPages = Math.ceil(totalCount / pageSize) || 1;

  useEffect(() => {
    console.log(`📁 ActiveTab: ${activeTab}`);
    console.log("📦 Products from server:", products);
    console.log(`📊 Total count: ${totalCount}, Total pages: ${totalPages}`);
  }, [activeTab, products, totalCount, totalPages]);

  // Reset to first page when category or search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [activeTab, debouncedSearch]);

  const handleProductClick = async (product) => {
    const productId = String(product._id || product.id || product.product_id);

    const alreadyExists = selectedItems.find(
      (item) => String(item._id) === productId,
    );
    if (alreadyExists) return;

    try {
      let detailedProduct = productCache[productId];

      if (!detailedProduct) {
        if (demo) {
          detailedProduct = product;
        } else {
          detailedProduct = await fetchWebProductById(productId);
        }

        setProductCache((prev) => ({
          ...prev,
          [productId]: detailedProduct,
        }));
      }

      const enriched = {
        ...product,
        _id: productId,
        sku: String(
          detailedProduct.sku ??
            detailedProduct.SKU ??
            product.sku ??
            product.SKU ??
            "",
        ).trim(),
        Product_Name:
          detailedProduct.Product_Name ||
          detailedProduct.name ||
          detailedProduct.meta_title ||
          detailedProduct.model ||
          (detailedProduct.sku ? `SKU ${detailedProduct.sku}` : null) ||
          product.Product_Name ||
          product.name ||
          "Unnamed",

        // Use discounted price if available, otherwise use regular price
        Price: Number(
          product.Price ??
            detailedProduct.Price ??
            detailedProduct.price ??
            product.price ??
            0,
        ),

        // Preserve discount information for display and calculations
        OriginalPrice:
          product.OriginalPrice ??
          Number(detailedProduct.price ?? detailedProduct.Price ?? 0),
        DiscountPrice: product.DiscountPrice ?? null,
        DiscountPercentage: product.DiscountPercentage ?? 0,
        HasDiscount: product.HasDiscount ?? false,

        quantity: 1,

        stockQty: Number(
          detailedProduct.Qty ??
            detailedProduct.quantity ??
            product.Qty ??
            product.quantity ??
            Infinity,
        ),

        isVariant: Boolean(detailedProduct.isVariant ?? product.isVariant),
      };

      if (!demo && enriched.stockQty <= 0) {
        showUiAlert(window.translate("This item is out of stock."), {
          tone: "warning",
        });
        return;
      }

      setSelectedItems((prev) => [...prev, enriched]);
    } catch (error) {
      if (noStore403(error)) {
        setDemo(true);
        setSelectedItems((prev) => [
          ...prev,
          {
            ...product,
            _id: productId,
            sku: String(product.sku ?? product.SKU ?? "").trim(),
            Product_Name: product.Product_Name || product.name || "Unnamed",
            Price: Number(product.Price ?? product.price ?? 0),
            quantity: 1,
            stockQty: Number(product.Qty ?? product.quantity ?? Infinity),
          },
        ]);
        return;
      }
      console.error("Failed to fetch product details:", error);
    }
  };

  const handleQuantityChange = (id, newQty) => {
    const idStr = String(id);

    setSelectedItems((prev) => {
      const exists = prev.find((item) => String(item._id) === idStr);
      if (!exists) return prev;

      if (newQty <= 0) {
        return prev.filter((i) => String(i._id) !== idStr);
      }

      const max = Number(exists.stockQty ?? Infinity);
      if (newQty > max) {
        const stockMsg =
          resolveUiLang() === "ar"
            ? `المتبقي ${max} قطعة فقط من "${exists.Product_Name}".`
            : `Only ${max} left in stock for "${exists.Product_Name}".`;
        showUiAlert(stockMsg, { tone: "warning" });
        newQty = max;
      }

      return prev.map((item) =>
        String(item._id) === idStr ? { ...item, quantity: newQty } : item,
      );
    });
  };

  useEffect(() => {
    console.log("🛒 Updated selectedItems:", selectedItems);
  }, [selectedItems]);

  const handleRemoveItem = (id) => {
    const idStr = String(id);

    setSelectedItems((prev) => {
      const newSelectedItems = prev.filter(
        (item) => String(item._id) !== idStr,
      );

      if (newSelectedItems.length === 0) {
        console.log("🛒 Cart is empty!");
      }

      return newSelectedItems;
    });

    setProducts((prev) =>
      prev.map((item) =>
        String(item._id) === idStr ? { ...item, quantity: 0 } : item,
      ),
    );
  };

  // tax details
  const subtotal = selectedItems.reduce(
    (sum, item) => sum + item.Price * item.quantity,
    0,
  );
  const shipping = 0;

  const tax = selectedItems.reduce((sum, item) => {
    const taxVal = parseFloat(item.taxType) || 0;
    return sum + (taxVal / 100) * item.Price * item.quantity;
  }, 0);

  const discount = selectedItems.reduce((sum, item) => {
    const discountVal = parseFloat(item.discountValue) || 0;
    return sum + (discountVal / 100) * item.Price * item.quantity;
  }, 0);

  // const roundoff = 0.11;

  // const totalPayable = subtotal + shipping + tax - coupon - discount + roundoff;
  // const roundoff = 0.11; // ❌ removed

  // Coupon Discount Calculation
  useEffect(() => {
    if (couponData && subtotal > 0) {
      let val = 0;
      if (couponData.type === "P") {
        // Percentage discount
        val = (subtotal * parseFloat(couponData.discount)) / 100;
      } else if (couponData.type === "F") {
        // Fixed amount discount
        val = parseFloat(couponData.discount);
      }
      // Ensure discount doesn't exceed subtotal
      setCouponDiscount(Math.min(val, subtotal));
    } else {
      setCouponDiscount(0);
    }
  }, [couponData, subtotal]);

  // Handle Apply Coupon
  const handleApplyCoupon = async () => {
    if (!couponCode.trim()) {
      showUiAlert(window.translate("Please enter a coupon code."), {
        tone: "warning",
      });
      return;
    }

    setLoadingCoupon(true);
    try {
      const res = await verifyCoupon(couponCode);

      if (res.status === "success") {
        setCouponData(res.data);
        showUiAlert(window.translate("Coupon applied successfully!"), {
          tone: "success",
        });
      } else {
        showUiAlert(window.translate("Invalid coupon code."), {
          tone: "error",
        });
        setCouponData(null);
        setCouponDiscount(0);
      }
    } catch (error) {
      console.error("Coupon Error:", error);
      showUiAlert(window.translate("Error applying coupon. Please try again."), {
        tone: "error",
      });
      setCouponData(null);
      setCouponDiscount(0);
    } finally {
      setLoadingCoupon(false);
    }
  };

  // Handle Remove Coupon
  const handleRemoveCoupon = () => {
    setCouponData(null);
    setCouponCode("");
    setCouponDiscount(0);
    showUiAlert(window.translate("Coupon removed."), { tone: "info" });
  };

  const totalPayable = subtotal + shipping + tax - discount - couponDiscount; // ✅ Updated with couponDiscount

  useEffect(() => {
    setPayingAmount(Number(totalPayable || 0));
  }, [totalPayable]);

  //   Quantity change handler for CartCounter
  const QuantityCounter = ({ value, onChange, max = Infinity }) => {
    const handleDecrement = () => {
      if (value > 1) onChange(value - 1);
    };
    const handleIncrement = () => {
      if (value < max) onChange(value + 1);
    };
    const atMax = value >= max;

    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          border: "1px solid #ccc",
          borderRadius: "30px",
          overflow: "hidden",
          height: "36px",
          width: "100px",
          backgroundColor: "#fff",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={handleDecrement}
          style={{
            width: "32px",
            height: "100%",
            border: "none",
            backgroundColor: "#f0f0f0",
            fontSize: "18px",
            fontWeight: "bold",
            cursor: "pointer",
          }}
        >
          −
        </button>
        <div
          style={{
            flex: 1,
            textAlign: "center",
            fontSize: "16px",
            fontWeight: "500",
            userSelect: "none",
          }}
        >
          {value}
        </div>
        <button
          onClick={handleIncrement}
          disabled={atMax}
          title={atMax ? "No more stock" : ""}
          style={{
            width: "32px",
            height: "100%",
            border: "none",
            backgroundColor: "#f0f0f0",
            fontSize: "18px",
            fontWeight: "bold",
            cursor: "pointer",
          }}
        >
          +
        </button>
      </div>
    );
  };

  QuantityCounter.propTypes = {
    value: PropTypes.number.isRequired,
    onChange: PropTypes.func.isRequired,
    max: PropTypes.number,
  };

  // Calculate change when receivedAmount or payingAmount changes
  useEffect(() => {
    const r = parseFloat(receivedAmount) || 0;
    const p = parseFloat(payingAmount) || 0;
    setChange(r - p > 0 ? r - p : 0);
  }, [receivedAmount, payingAmount]);

  // Handle payment type selection (before opening modal)
  const paymentTypeMap = {
    cash: { value: "cash", label: "Cash" },
    knet: { value: "knet", label: "KNET" },
    card: { value: "card", label: "Card" },
    split: { value: "split", label: "Split" },
  };

  const handlePaymentTypeSelect = (type) => {
    setPaymentType(paymentTypeMap[type] || paymentTypeMap.cash);
    if (type === "cash") {
      setInvoiceNo(""); // No reference needed for cash
    }
    if (type !== "split") {
      // Reset splits when switching away from split
      setPaymentSplits([
        { payment_type: "cash", amount: "", reference: "" },
        { payment_type: "card", amount: "", reference: "" },
      ]);
    }
  };

  // Update a split payment entry
  const handleSplitChange = (index, field, value) => {
    setPaymentSplits((prev) =>
      prev.map((s, i) => (i === index ? { ...s, [field]: value } : s)),
    );
  };

  // Handle Place Order submission - Uses POS Order API
  const handlePlaceOrder = async () => {
    if (isSubmitting) return;

    try {
      setIsSubmitting(true);

      if (!Array.isArray(selectedItems) || selectedItems.length === 0) {
        showUiAlert(window.translate("No items selected."), { tone: "warning" });
        return;
      }

      const validItems = selectedItems.filter(
        (item) => item && item._id && item.quantity > 0,
      );

      if (validItems.length === 0) {
        showUiAlert(window.translate("No valid items to process."), {
          tone: "warning",
        });
        return;
      }

      // Validate split payment totals
      if (paymentType?.value === "split") {
        const splitTotal = paymentSplits.reduce(
          (sum, s) => sum + (parseFloat(s.amount) || 0),
          0,
        );
        if (Math.abs(splitTotal - totalPayable) > 0.01) {
          showUiAlert(
            window.translate(
              "Split payment amounts must equal the total payable.",
            ) + ` (${splitTotal.toFixed(3)} ≠ ${totalPayable.toFixed(3)})`,
          { tone: "warning" },
          );
          return;
        }
      }

      // Build POS order payload
      const orderPayload = buildPosOrderPayload({
        selectedItems: validItems,
        customerInfo,
        totalPayable: parseFloat(totalPayable),
        amountPaid: parseFloat(totalPayable),
        paymentType: paymentType?.value || "cash",
        discountAmount: couponDiscount + discount,
        taxAmount: tax,
        notes: saleNote || paymentNote || "",
        couponData: couponData || null,
        paymentSplits: paymentType?.value === "split" ? paymentSplits : [],
      });

      console.log("📤 Sending POS order payload:", orderPayload);

      // Call the POS create order API
      const res = await createPosOrder(orderPayload);

      console.log("📥 POS Order API response:", res);

      // Check for success
      if (res.success || res.data?.pos_order_id || res.data?.order_id) {
        const orderId = res.data?.order_id || res.order_id || null;
        const posOrderId =
          res.data?.pos_order_id || res.pos_order_id || orderId || `POS-${Date.now()}`;
        const responsePaymentSplits = Array.isArray(res?.data?.payment_splits)
          ? res.data.payment_splits
          : [];
        const requestPaymentSplits =
          paymentType?.value === "split" && Array.isArray(paymentSplits)
            ? paymentSplits
            : [];

        // Create transaction object for invoice
        const tx = {
          orderId: orderId || posOrderId,
          type: paymentType?.label || "Cash",
          invoiceNo:
            paymentType?.value === "knet" || paymentType?.value === "card"
              ? invoiceNo
              : "",
          receivedAmount: parseFloat(receivedAmount) || totalPayable,
          payingAmount: parseFloat(payingAmount) || totalPayable,
          change:
            res.data?.change_given != null
              ? parseFloat(res.data.change_given)
              : parseFloat(change) || 0,
          paymentReceiver,
          totalAmount: parseFloat(totalPayable),
          createdAt: new Date().toISOString(),
          items: validItems.map((item) => ({
            productId: item._id,
            name: item.Product_Name || item.name,
            quantity: item.quantity,
            price: item.Price,
          })),
          paymentSplits:
            responsePaymentSplits.length > 0
              ? responsePaymentSplits
              : requestPaymentSplits,
          couponDiscount: couponDiscount,
          couponCode: couponData?.code,
        };

        setLastTransaction(tx);
        showUiAlert(
          `${window.translate("Order ID")}:- ${orderId || "-"}`,
          {
            tone: "success",
            centerMessage: true,
            title: window.translate("Order Created Successfully"),
            messageClassName: "pos-ui-alert-order-id",
          },
        );

        // Reset cart and forms
        setSelectedItems([]);
        setReceivedAmount("");
        setPayingAmount(0);
        setChange(0);
        setPaymentReceiver("");
        setPaymentNote("");
        setSaleNote("");
        setStaffNote("");
        setInvoiceNo("");
        setCouponCode("");
        setCouponData(null);
        setCouponDiscount(0);
        setPaymentType({ value: "cash", label: "Cash" });
        setPaymentSplits([
          { payment_type: "cash", amount: "", reference: "" },
          { payment_type: "card", amount: "", reference: "" },
        ]);
        setCustomerInfo({
          firstname: "",
          lastname: "",
          email: "",
          telephone: "",
          company: "",
          address_1: "",
          address_2: "",
          city: "Kuwait City",
          postcode: "",
          country_id: 114,
          zone_id: 1789,
          area_id: 153,
        });
      } else {
        throw new Error(res.message || res.error || "Order creation failed");
      }
    } catch (err) {
      console.error("❌ Order Error:", err);
      const errorMsg =
        err?.response?.data?.message ||
        err?.message ||
        "Failed to create order";
      showUiAlert(window.translate("Failed to submit order: ") + errorMsg, {
        tone: "error",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Render product card
  const renderProductCard = (product) => {
    const isSelected = selectedItems.some(
      (item) => String(item._id) === String(product._id),
    );
    const isOutOfStock =
      Number(product.Qty ?? product.stockQty ?? product.quantity ?? 0) <= 0;

    return (
      <div
        key={product._id}
        className="col-sm-6 col-md-6 col-lg-6 col-xl-4 col-xxl-3"
      >
        <div
          className={`product-info card mb-0 ${isSelected ? "active" : ""}`}
          data-product-id={product._id}
          onClick={() => handleProductClick(product)}
          tabIndex="0"
        >
          {isOutOfStock && (
            <span className="badge bg-danger pos-stock-badge">
              {window.translate("Out of Stock")}
            </span>
          )}
          <Link to="#" className="pro-img">
            <img
              src={product.image}
              style={{ width: "160px", height: "160px", objectFit: "cover" }}
              alt={product.Product_Name}
              onError={(e) => (e.target.src = "/assets/img/products/shoes.jpg")}
            />
            <span>
              <i className="ti ti-circle-check-filled" />
            </span>
          </Link>

          <h6 className="cat-name">
            <Link to="#">{product.Category || "Uncategorized"}</Link>
          </h6>

          <h6 className="product-name">
            <Link to="#">{product.Product_Name || product.name}</Link>
          </h6>

          {product.allVariants?.length > 0 && (
            <div className="variant-buttons d-flex flex-wrap gap-1 mb-2 px-2">
              {product.allVariants.map((variant) => {
                const isVariantSelected = selectedItems.some(
                  (item) => String(item._id) === String(variant._id),
                );

                return (
                  <button
                    key={variant._id}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleProductClick(variant);
                    }}
                    className={`btn btn-sm px-3 py-1 ${
                      isVariantSelected
                        ? "btn-primary text-white"
                        : "btn-outline-secondary"
                    }`}
                    style={{
                      borderRadius: "20px",
                      fontSize: "12px",
                      textTransform: "capitalize",
                    }}
                  >
                    {variant.name}
                  </button>
                );
              })}
            </div>
          )}

          <div className="d-flex align-items-center justify-content-between price px-2">
            <div className="price-wrapper">
              {product.HasDiscount ? (
                <>
                  <span
                    className="original-price text-muted text-decoration-line-through me-2"
                    style={{ fontSize: "12px" }}
                  >
                    {Number(product.OriginalPrice || 0).toFixed(2)}
                  </span>
                  <span
                    className="discount-price fw-bold"
                    style={{ color: "#28a745", fontSize: "14px" }}
                  >
                    {Number(product.Price || 0).toFixed(2)}
                  </span>
                  {product.DiscountPercentage > 0 && (
                    <span
                      className="discount-badge ms-2 badge"
                      style={{
                        backgroundColor: "#dc3545",
                        color: "#fff",
                        fontSize: "10px",
                        padding: "2px 6px",
                        borderRadius: "4px",
                      }}
                    >
                      -{product.DiscountPercentage}%
                    </span>
                  )}
                </>
              ) : (
                <p className="text-gray-9 mb-0">
                  {Number(product.Price || 0).toFixed(2)}
                </p>
              )}
            </div>
            <div className="qty-item m-0">
              <QuantityCounter
                value={
                  selectedItems.find(
                    (item) => String(item._id) === String(product._id),
                  )?.quantity ?? 1
                }
                max={
                  selectedItems.find(
                    (item) => String(item._id) === String(product._id),
                  )?.stockQty ?? Infinity
                }
                onChange={(newQty) => handleQuantityChange(product._id, newQty)}
              />
            </div>
          </div>
        </div>
      </div>
    );
  };

  const alertIconByTone = {
    success: "ti ti-circle-check-filled",
    error: "ti ti-alert-circle",
    warning: "ti ti-alert-triangle",
    info: "ti ti-info-circle",
  };
  const alertIconClass = alertIconByTone[uiAlert.tone] || alertIconByTone.info;

  return (
    <div className="main-wrapper pos-five">
      <div className="page-wrapper pos-pg-wrapper me-0 ms-0">
        <div className="content pos-design p-0">
          <div className="row pos-wrapper">
            {/* Products */}
            <div className="col-12 col-lg-7 col-xl-8 d-flex">
              <div className="pos-categories tabs_wrapper p-0 flex-fill">
                <div className="content-wrap">
                  <div className="tab-wrap mt-5">
                    <ul className="tabs owl-carousel pos-category5">
                      {categories.map((cat) => (
                        <li
                          key={cat._id}
                          onClick={() => {
                            console.log(`🟡 Category Selected: ${cat._id}`);
                            setActiveTab(cat._id);
                          }}
                          className={activeTab === cat._id ? "active" : ""}
                        >
                          <Link to="#">
                            {cat.hasImage && cat.isLocalAsset ? (
                              <ImageWithBasePath
                                src={cat.imageUrl}
                                alt="Category"
                              />
                            ) : cat.hasImage && cat.imageUrl ? (
                              <>
                                <img
                                  src={cat.imageUrl}
                                  alt="Category"
                                  style={{
                                    width: "50px",
                                    height: "50px",
                                    objectFit: "cover",
                                    borderRadius: "8px",
                                  }}
                                  onError={(e) => {
                                    e.currentTarget.style.display = "none";
                                    if (e.currentTarget.nextElementSibling) {
                                      e.currentTarget.nextElementSibling.style.display =
                                        "flex";
                                    }
                                  }}
                                />
                                <div
                                  className="category-icon-fallback"
                                  style={{
                                    display: "none",
                                    width: "50px",
                                    height: "50px",
                                    backgroundColor: "#f0f4ff",
                                    borderRadius: "8px",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    fontSize: "24px",
                                    color: "#5b6af0",
                                  }}
                                >
                                  <i className="ti ti-category" />
                                </div>
                              </>
                            ) : (
                              <div
                                className="category-icon-fallback"
                                style={{
                                  display: "flex",
                                  width: "50px",
                                  height: "50px",
                                  backgroundColor: "#f0f4ff",
                                  borderRadius: "8px",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  fontSize: "24px",
                                  color: "#5b6af0",
                                }}
                              >
                                <i className="ti ti-category" />
                              </div>
                            )}
                          </Link>
                          <h6
                            style={{
                              maxWidth: "80px",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              fontSize: "12px",
                              textAlign: "center",
                              marginTop: "4px",
                            }}
                            title={cat.name}
                          >
                            <Link to="#">{cat.name}</Link>
                          </h6>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="tab-content-wrap">
                    <div className="d-flex align-items-center justify-content-between flex-wrap mb-2 mt-5">
                      <div className="mb-3">
                        <h5 className="mb-1">{window.translate("Welcome")}</h5>
                        <p>
                          {new Date().toLocaleDateString("en-US", {
                            month: "long",
                            day: "numeric",
                            year: "numeric",
                          })}
                        </p>
                      </div>
                      {/* Search Input */}
                      <div
                        className="search-box mb-3"
                        style={{ minWidth: "280px" }}
                      >
                        <div className="input-group">
                          <span className="input-group-text bg-white border-end-0">
                            <i className="ti ti-search" />
                          </span>
                          <input
                            type="text"
                            className="form-control border-start-0"
                            placeholder={window.translate(
                              "Search products, SKU...",
                            )}
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                          />
                          {searchQuery && (
                            <button
                              type="button"
                              className="btn btn-outline-secondary border-start-0"
                              onClick={() => setSearchQuery("")}
                              style={{ borderColor: "#ced4da" }}
                            >
                              <i className="ti ti-x" />
                            </button>
                          )}
                        </div>
                        {isLoading && (
                          <small className="text-muted">
                            <span className="spinner-border spinner-border-sm me-1" />
                            {window.translate("Loading...")}
                          </small>
                        )}
                      </div>
                    </div>
                    <div className="pos-products">
                      <div className="tabs_container">
                        <div
                          className={`tab_content ${activeTab} active`}
                          data-tab={activeTab}
                        >
                          <div className="row g-3">
                            {isLoading ? (
                              <div className="col-12 text-center py-5">
                                <div
                                  className="spinner-border text-primary"
                                  role="status"
                                >
                                  <span className="visually-hidden">
                                    Loading...
                                  </span>
                                </div>
                                <p className="mt-2 text-muted">
                                  {window.translate("Loading products...")}
                                </p>
                              </div>
                            ) : displayProducts.length > 0 ? (
                              displayProducts.map(renderProductCard)
                            ) : (
                              <div className="col-12">
                                <p className="text-muted fs-3">
                                  {window.translate("No products found")}
                                </p>
                              </div>
                            )}
                          </div>

                          {/* Pagination */}
                          {!isLoading && totalCount > 0 && totalPages > 1 && (
                            <div className="d-flex flex-wrap justify-content-between align-items-center mt-3 pos-pagination">
                              <div className="text-muted small mb-2 mb-md-0">
                                {(() => {
                                  const start =
                                    (currentPage - 1) * pageSize + 1;
                                  const end = Math.min(
                                    currentPage * pageSize,
                                    totalCount,
                                  );
                                  return (
                                    window.translate("Showing") +
                                    ` ${start}-${end} ${window.translate(
                                      "of",
                                    )} ${totalCount}`
                                  );
                                })()}
                              </div>

                              <nav>
                                <ul className="pagination mb-0">
                                  <li
                                    className={`page-item ${
                                      currentPage === 1 ? "disabled" : ""
                                    }`}
                                  >
                                    <button
                                      className="page-link"
                                      onClick={() =>
                                        currentPage > 1 &&
                                        setCurrentPage(currentPage - 1)
                                      }
                                    >
                                      «
                                    </button>
                                  </li>

                                  {Array.from(
                                    { length: totalPages },
                                    (_, i) => i + 1,
                                  )
                                    .filter((page) => {
                                      // show current, first, last and +/-2 around current
                                      if (page === 1 || page === totalPages)
                                        return true;
                                      return Math.abs(page - currentPage) <= 2;
                                    })
                                    .map((page, idx, pages) => {
                                      const prevPage = pages[idx - 1];
                                      const showEllipsis =
                                        prevPage && page - prevPage > 1;

                                      return (
                                        <React.Fragment key={page}>
                                          {showEllipsis && (
                                            <li className="page-item disabled">
                                              <span className="page-link">
                                                …
                                              </span>
                                            </li>
                                          )}
                                          <li
                                            className={`page-item ${
                                              page === currentPage
                                                ? "active"
                                                : ""
                                            }`}
                                          >
                                            <button
                                              className="page-link"
                                              onClick={() =>
                                                setCurrentPage(page)
                                              }
                                            >
                                              {page}
                                            </button>
                                          </li>
                                        </React.Fragment>
                                      );
                                    })}

                                  <li
                                    className={`page-item ${
                                      currentPage === totalPages
                                        ? "disabled"
                                        : ""
                                    }`}
                                  >
                                    <button
                                      className="page-link"
                                      onClick={() =>
                                        currentPage < totalPages &&
                                        setCurrentPage(currentPage + 1)
                                      }
                                    >
                                      »
                                    </button>
                                  </li>
                                </ul>
                              </nav>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            {/* /Products */}

            {/* Order Details */}
            <div className="col-12 col-lg-5 col-xl-4 ps-0 d-lg-flex">
              <aside className="product-order-list bg-secondary-transparent flex-fill w-100">
                <div className="card">
                  <div className="card-body">
                    <div className="order-head d-flex align-items-center justify-content-between w-100">
                      <div>
                        <h3>{window.translate("Order List")}</h3>
                      </div>
                    </div>

                    {/* Customer Info Section */}
                    <div className="customer-info block-section mt-3">
                      <h5 className="mb-2">
                        {window.translate("Customer Information")}
                      </h5>
                      <div className="row g-2">
                        <div className="col-6">
                          <input
                            type="text"
                            className="form-control form-control-sm"
                            placeholder={window.translate("First Name")}
                            value={customerInfo.firstname}
                            onChange={(e) =>
                              setCustomerInfo((prev) => ({
                                ...prev,
                                firstname: e.target.value,
                              }))
                            }
                          />
                        </div>
                        <div className="col-6">
                          <input
                            type="text"
                            className="form-control form-control-sm"
                            placeholder={window.translate("Last Name")}
                            value={customerInfo.lastname}
                            onChange={(e) =>
                              setCustomerInfo((prev) => ({
                                ...prev,
                                lastname: e.target.value,
                              }))
                            }
                          />
                        </div>
                        <div className="col-6">
                          <input
                            type="tel"
                            className="form-control form-control-sm"
                            placeholder={window.translate("Phone")}
                            value={customerInfo.telephone}
                            onChange={(e) =>
                              setCustomerInfo((prev) => ({
                                ...prev,
                                telephone: e.target.value,
                              }))
                            }
                          />
                        </div>
                        <div className="col-6">
                          <input
                            type="email"
                            className="form-control form-control-sm"
                            placeholder={window.translate("Email")}
                            value={customerInfo.email}
                            onChange={(e) =>
                              setCustomerInfo((prev) => ({
                                ...prev,
                                email: e.target.value,
                              }))
                            }
                          />
                        </div>
                      </div>
                    </div>

                    <div className="product-added block-section">
                      <div className="head-text d-flex align-items-center justify-content-between mb-3">
                        <div className="d-flex align-items-center gap-2">
                          <h5 className="me-2">
                            {window.translate("Order Details")}
                          </h5>
                          <div className="badge bg-light text-gray-9 fs-12 fw-semibold py-2 border rounded">
                            {window.translate("Items")} :{" "}
                            <span className="text-teal">
                              {selectedItems.length}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="product-wrap">
                        <div className="empty-cart">
                          <div className="fs-24 mb-1">
                            <i className="ti ti-shopping-cart" />
                          </div>
                          <p className="fw-bold">
                            {window.translate("No Products Selected")}
                          </p>
                        </div>
                        <div className="product-list border-0 p-0">
                          <div className="table-responsive">
                            <table className="table table-borderless">
                              <thead>
                                <tr>
                                  <th className="fw-bold bg-light">
                                    {window.translate("Item")}
                                  </th>
                                  <th className="fw-bold bg-light">
                                    {window.translate("SKU")}
                                  </th>
                                  <th className="fw-bold bg-light">
                                    {window.translate("QTY")}
                                  </th>
                                  <th className="fw-bold bg-light text-end">
                                    {window.translate("Cost")}
                                  </th>
                                </tr>
                              </thead>
                              <tbody>
                                {selectedItems.map((item) => (
                                  <tr key={`${item._id}-${item.quantity}`}>
                                    <td>
                                      <div className="d-flex align-items-center">
                                        <Link
                                          className="delete-icon"
                                          to="#"
                                          onClick={() =>
                                            handleRemoveItem(item._id)
                                          }
                                        >
                                          <i className="ti ti-trash-x-filled" />
                                        </Link>
                                        <h6 className="fs-13 fw-normal">
                                          <Link to="#" className="link-default">
                                            {item.Product_Name || item.name}
                                          </Link>
                                        </h6>
                                      </div>
                                    </td>
                                    <td className="fs-13 fw-normal text-gray-9">
                                      {item.sku || "-"}
                                    </td>
                                    <td>
                                      <div className="qty-item m-0">
                                        <div
                                          style={{
                                            display: "flex",
                                            alignItems: "center",
                                            border: "1px solid #ccc",
                                            borderRadius: "30px",
                                            overflow: "hidden",
                                            height: "36px",
                                            width: "100px",
                                            backgroundColor: "#fff",
                                          }}
                                        >
                                          <button
                                            onClick={() => {
                                              if (item.quantity > 1) {
                                                handleQuantityChange(
                                                  item._id,
                                                  item.quantity - 1,
                                                );
                                              }
                                            }}
                                            style={{
                                              width: "32px",
                                              height: "100%",
                                              border: "none",
                                              backgroundColor: "#f0f0f0",
                                              fontSize: "18px",
                                              fontWeight: "bold",
                                              cursor: "pointer",
                                            }}
                                          >
                                            −
                                          </button>
                                          <div
                                            style={{
                                              flex: 1,
                                              textAlign: "center",
                                              fontSize: "16px",
                                              fontWeight: "500",
                                              userSelect: "none",
                                            }}
                                          >
                                            {item.quantity}
                                          </div>
                                          <button
                                            onClick={() => {
                                              handleQuantityChange(
                                                item._id,
                                                item.quantity + 1,
                                              );
                                            }}
                                            style={{
                                              width: "32px",
                                              height: "100%",
                                              border: "none",
                                              backgroundColor: "#f0f0f0",
                                              fontSize: "18px",
                                              fontWeight: "bold",
                                              cursor: "pointer",
                                            }}
                                          >
                                            +
                                          </button>
                                        </div>
                                      </div>
                                    </td>
                                    <td className="fs-13 fw-semibold text-gray-9 text-end">
                                      {(item.Price * item.quantity).toFixed(2)}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="order-total bg-total bg-white p-0">
                      <h5 className="mb-3">
                        {window.translate("Payment Summary")}
                      </h5>

                      {/* Coupon Section */}
                      <div className="mb-3">
                        <label className="form-label">
                          {window.translate("Coupon Code")}
                        </label>
                        <div
                          className="input-group"
                          style={{ direction: "ltr" }}
                        >
                          <input
                            type="text"
                            className="form-control"
                            placeholder={window.translate("Enter Code")}
                            value={couponCode}
                            onChange={(e) => setCouponCode(e.target.value)}
                            disabled={!!couponData}
                          />
                          {couponData ? (
                            <button
                              className="btn btn-danger"
                              onClick={handleRemoveCoupon}
                              type="button"
                            >
                              <i className="ti ti-x" />
                            </button>
                          ) : (
                            <button
                              className="btn btn-primary"
                              onClick={handleApplyCoupon}
                              disabled={loadingCoupon}
                              type="button"
                            >
                              {loadingCoupon ? (
                                <span className="spinner-border spinner-border-sm" />
                              ) : (
                                window.translate("Apply")
                              )}
                            </button>
                          )}
                        </div>
                        {couponData && (
                          <small className="text-success d-block mt-1">
                            {window.translate("Coupon applied")}:{" "}
                            {couponData.name} ({couponData.code})
                          </small>
                        )}
                      </div>
                      {selectedItems.length > 0 && (
                        <table className="table table-responsive table-borderless">
                          <tbody>
                            <tr>
                              <td>{window.translate("Tax")}</td>
                              <td className="text-gray-9 text-end">
                                {tax.toFixed(2)}
                              </td>
                            </tr>

                            {/* Coupon Discount Row */}
                            {couponDiscount > 0 && (
                              <tr>
                                <td>
                                  <span className="text-success">
                                    {window.translate("Coupon Discount")}
                                  </span>
                                </td>
                                <td className="text-success text-end">
                                  -{couponDiscount.toFixed(2)}
                                </td>
                              </tr>
                            )}
                            {/* <tr>
                              <td>
                                <div className="form-check form-switch p-0">
                                  <label
                                    className="form-check-label"
                                    htmlFor="round"
                                  >
                                    {window.translate("Roundoff")}
                                  </label>
                                </div>
                              </td>
                              <td className="text-gray-9 text-end">
                                +{roundoff.toFixed(2)}
                              </td>
                            </tr> */}
                            <tr>
                              <td>{window.translate("Sub Total")}</td>
                              <td className="text-gray-9 text-end">
                                {subtotal.toFixed(2)}
                              </td>
                            </tr>
                            <tr>
                              <td className="fw-bold border-top border-dashed">
                                {window.translate("Total Payable")}
                              </td>
                              <td className="text-gray-9 fw-bold text-end border-top border-dashed">
                                {totalPayable.toFixed(2)}
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      )}
                    </div>
                  </div>
                </div>

                {/* Payment Method Selection */}
                <div className="card payment-method">
                  <div className="card-body">
                    <h5 className="mb-3">
                      {window.translate("Select Payment")}
                    </h5>
                    <div className="row align-items-center methods g-2">
                      {/* Cash */}
                      <div className="col-6 col-md-3 d-flex">
                        <Link
                          to="#"
                          className={`payment-item d-flex align-items-center justify-content-center p-2 flex-fill ${
                            paymentType?.value === "cash"
                              ? "border-primary bg-primary-light"
                              : ""
                          }`}
                          data-bs-toggle="modal"
                          data-bs-target="#payment-cash"
                          onClick={() => handlePaymentTypeSelect("cash")}
                          style={{
                            borderRadius: "8px",
                            border:
                              paymentType?.value === "cash"
                                ? "2px solid #0d6efd"
                                : "1px solid #dee2e6",
                          }}
                        >
                          <i
                            className={`ti ti-cash me-2 fs-5 ${
                              paymentType?.value === "cash"
                                ? "text-primary"
                                : "text-success"
                            }`}
                          />
                          <p className="fs-14 fw-medium mb-0">
                            {window.translate("Cash")}
                          </p>
                        </Link>
                      </div>

                      {/* KNET */}
                      <div className="col-6 col-md-3 d-flex">
                        <Link
                          to="#"
                          className={`payment-item d-flex align-items-center justify-content-center p-2 flex-fill ${
                            paymentType?.value === "knet"
                              ? "border-primary bg-primary-light"
                              : ""
                          }`}
                          onClick={(e) => {
                            e.preventDefault();
                            handlePaymentTypeSelect("knet");
                          }}
                          style={{
                            borderRadius: "8px",
                            border:
                              paymentType?.value === "knet"
                                ? "2px solid #0d6efd"
                                : "1px solid #dee2e6",
                          }}
                        >
                          <i
                            className={`ti ti-device-mobile me-2 fs-5 ${
                              paymentType?.value === "knet"
                                ? "text-primary"
                                : "text-warning"
                            }`}
                          />
                          <p className="fs-14 fw-medium mb-0">
                            {window.translate("KNET")}
                          </p>
                        </Link>
                      </div>

                      {/* Card */}
                      <div className="col-6 col-md-3 d-flex">
                        <Link
                          to="#"
                          className={`payment-item d-flex align-items-center justify-content-center p-2 flex-fill ${
                            paymentType?.value === "card"
                              ? "border-primary bg-primary-light"
                              : ""
                          }`}
                          onClick={(e) => {
                            e.preventDefault();
                            handlePaymentTypeSelect("card");
                          }}
                          style={{
                            borderRadius: "8px",
                            border:
                              paymentType?.value === "card"
                                ? "2px solid #0d6efd"
                                : "1px solid #dee2e6",
                          }}
                        >
                          <i
                            className={`ti ti-credit-card me-2 fs-5 ${
                              paymentType?.value === "card"
                                ? "text-primary"
                                : "text-info"
                            }`}
                          />
                          <p className="fs-14 fw-medium mb-0">
                            {window.translate("Card")}
                          </p>
                        </Link>
                      </div>

                      {/* Split */}
                      <div className="col-6 col-md-3 d-flex">
                        <Link
                          to="#"
                          className={`payment-item d-flex align-items-center justify-content-center p-2 flex-fill ${
                            paymentType?.value === "split"
                              ? "border-primary bg-primary-light"
                              : ""
                          }`}
                          onClick={(e) => {
                            e.preventDefault();
                            handlePaymentTypeSelect("split");
                          }}
                          style={{
                            borderRadius: "8px",
                            border:
                              paymentType?.value === "split"
                                ? "2px solid #0d6efd"
                                : "1px solid #dee2e6",
                          }}
                        >
                          <i
                            className={`ti ti-arrows-split me-2 fs-5 ${
                              paymentType?.value === "split"
                                ? "text-primary"
                                : "text-secondary"
                            }`}
                          />
                          <p className="fs-14 fw-medium mb-0">
                            {window.translate("Split")}
                          </p>
                        </Link>
                      </div>
                    </div>

                    {/* Reference Field - For KNET / Card */}
                    {(paymentType?.value === "knet" ||
                      paymentType?.value === "card") && (
                      <div className="mt-3">
                        <label className="form-label fw-semibold">
                          {window.translate("Reference Number")}
                        </label>
                        <div className="input-group">
                          <span className="input-group-text">
                            <i className="ti ti-file-invoice" />
                          </span>
                          <input
                            type="text"
                            className="form-control"
                            value={invoiceNo}
                            onChange={(e) => setInvoiceNo(e.target.value)}
                            placeholder={window.translate(
                              "Enter Transaction/Reference Number",
                            )}
                          />
                        </div>
                      </div>
                    )}

                    {/* Split Payment Fields */}
                    {paymentType?.value === "split" && (
                      <div className="mt-3">
                        <label className="form-label fw-semibold">
                          {window.translate("Split Payment Details")}
                        </label>
                        {paymentSplits.map((split, idx) => (
                          <div
                            key={idx}
                            className="d-flex align-items-center gap-2 mb-2"
                          >
                            <select
                              className="form-select form-select-sm"
                              style={{ maxWidth: "110px" }}
                              value={split.payment_type}
                              onChange={(e) =>
                                handleSplitChange(
                                  idx,
                                  "payment_type",
                                  e.target.value,
                                )
                              }
                            >
                              <option value="cash">
                                {window.translate("Cash")}
                              </option>
                              <option value="knet">
                                {window.translate("KNET")}
                              </option>
                              <option value="card">
                                {window.translate("Card")}
                              </option>
                            </select>
                            <input
                              type="number"
                              className="form-control form-control-sm"
                              placeholder={window.translate("Amount")}
                              value={split.amount}
                              onChange={(e) =>
                                handleSplitChange(idx, "amount", e.target.value)
                              }
                              step="0.001"
                              min="0"
                            />
                            <input
                              type="text"
                              className="form-control form-control-sm"
                              placeholder={window.translate("Ref#")}
                              value={split.reference}
                              onChange={(e) =>
                                handleSplitChange(
                                  idx,
                                  "reference",
                                  e.target.value,
                                )
                              }
                              style={{ maxWidth: "100px" }}
                            />
                            {paymentSplits.length > 2 && (
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-danger"
                                onClick={() =>
                                  setPaymentSplits((prev) =>
                                    prev.filter((_, i) => i !== idx),
                                  )
                                }
                              >
                                <i className="ti ti-x" />
                              </button>
                            )}
                          </div>
                        ))}
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-primary"
                          onClick={() =>
                            setPaymentSplits((prev) => [
                              ...prev,
                              {
                                payment_type: "cash",
                                amount: "",
                                reference: "",
                              },
                            ])
                          }
                        >
                          <i className="ti ti-plus me-1" />
                          {window.translate("Add Split")}
                        </button>
                        <div className="mt-2">
                          <small
                            className={
                              Math.abs(
                                paymentSplits.reduce(
                                  (s, p) => s + (parseFloat(p.amount) || 0),
                                  0,
                                ) - totalPayable,
                              ) < 0.01
                                ? "text-success"
                                : "text-danger"
                            }
                          >
                            {window.translate("Split Total")}:{" "}
                            {paymentSplits
                              .reduce(
                                (s, p) => s + (parseFloat(p.amount) || 0),
                                0,
                              )
                              .toFixed(3)}{" "}
                            / {totalPayable.toFixed(3)}
                          </small>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <div className="btn-row d-flex align-items-center justify-content-between gap-3">
                  <div className="d-flex align-items-center flex-fill gap-2">
                    <select
                      className="form-select pd-3"
                      style={{
                        maxWidth: 150,
                        paddingTop: 10,
                        paddingBottom: 10,
                      }}
                      value={invoiceLang}
                      onChange={(e) => setInvoiceLang(e.target.value)}
                    >
                      <option value="en">
                        {window.translate("English Invoice")}
                      </option>
                      <option value="ar">
                        {window.translate("Arabic Invoice")}
                      </option>
                    </select>

                    <button
                      type="button"
                      className="btn btn-white d-flex align-items-center justify-content-center flex-fill m-0"
                      onClick={() => printInvoice(invoiceLang)}
                      title={window.translate("Print last successful invoice")}
                    >
                      <i className="ti ti-printer me-2 ms-2" />
                      {window.translate("Print Order")}
                    </button>
                  </div>

                  <button
                    type="button"
                    className="btn btn-secondary d-flex align-items-center justify-content-center flex-fill m-0"
                    onClick={handlePlaceOrder}
                    disabled={isSubmitting || selectedItems.length === 0}
                  >
                    {isSubmitting ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" />
                        {window.translate("Processing...")}
                      </>
                    ) : (
                      <>
                        <i className="ti ti-shopping-cart me-2 ms-2" />
                        {window.translate("Place Order")}
                      </>
                    )}
                  </button>
                </div>
              </aside>
            </div>
            {/* /Order Details */}
          </div>
        </div>
      </div>
      {uiAlert.open && (
        <div className="pos-ui-alert-overlay" onClick={closeUiAlert}>
          <div
            className={`pos-ui-alert pos-ui-alert-${uiAlert.tone}`}
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="pos-ui-alert-title"
            aria-describedby="pos-ui-alert-message"
            dir={uiAlert.lang === "ar" ? "rtl" : "ltr"}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              className="pos-ui-alert-close"
              onClick={closeUiAlert}
              aria-label={uiAlert.lang === "ar" ? "إغلاق النافذة" : "Close popup"}
            >
              <i className="ti ti-x" />
            </button>

            <div className="pos-ui-alert-body">
              <span className="pos-ui-alert-icon" aria-hidden="true">
                <i className={alertIconClass} />
              </span>
              <div className="pos-ui-alert-copy">
                <h5 id="pos-ui-alert-title">{uiAlert.title}</h5>
                <p
                  id="pos-ui-alert-message"
                  className={[
                    uiAlert.centerMessage ? "pos-ui-alert-message-centered" : "",
                    uiAlert.messageClassName,
                  ]
                    .filter(Boolean)
                    .join(" ")}
                >
                  {uiAlert.message}
                </p>
              </div>
            </div>

            <div className="pos-ui-alert-actions">
              <button
                type="button"
                className="pos-ui-alert-confirm"
                onClick={closeUiAlert}
                autoFocus
              >
                {uiAlert.confirmText}
              </button>
            </div>
          </div>
        </div>
      )}
      <PosModals
        selectedItems={selectedItems}
        totalAmount={totalPayable}
        receivedAmount={receivedAmount}
        setReceivedAmount={setReceivedAmount}
        payingAmount={payingAmount}
        setPayingAmount={setPayingAmount}
        change={change}
        setChange={setChange}
        paymentType={paymentType}
        setPaymentType={setPaymentType}
        paymentReceiver={paymentReceiver}
        setPaymentReceiver={setPaymentReceiver}
        paymentNote={paymentNote}
        setPaymentNote={setPaymentNote}
        saleNote={saleNote}
        setSaleNote={setSaleNote}
        staffNote={staffNote}
        setStaffNote={setStaffNote}
        handlePlaceOrder={handlePlaceOrder}
        // New props for invoice number
        invoiceNo={invoiceNo}
        setInvoiceNo={setInvoiceNo}
      />
    </div>
  );
};

export default Pos;
